// src/components/IntroScreen.js
// Cinematic intro sequence — pure Canvas + Web Audio, no external deps
import React, { useEffect, useRef, useCallback } from 'react';

const CYAN   = '#00f5ff';
const PURPLE = '#7c3aed';
const GOLD   = '#ffd700';
const GREEN  = '#39ff14';

// ── Typewriter helper ─────────────────────────────────────────
function typeText(ctx, text, x, y, charW, style, done) {
  let i = 0;
  ctx.save();
  Object.assign(ctx, style);
  const tick = () => {
    if (i > text.length) { ctx.restore(); done && done(); return; }
    ctx.clearRect(x, y - 28, charW * text.length + 20, 36);
    ctx.fillText(text.slice(0, i), x, y);
    // blinking cursor
    if (i < text.length) ctx.fillText('|', x + ctx.measureText(text.slice(0, i)).width + 2, y);
    i++;
    setTimeout(tick, 38);
  };
  tick();
}

export default function IntroScreen({ onComplete }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const doneRef   = useRef(false);
  const phaseRef  = useRef(0);

  const skip = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    cancelAnimationFrame(rafRef.current);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const onResize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', onResize);

    // ── Shared state ───────────────────────────────────────────
    let t = 0;          // time counter
    let phase = 0;      // 0=grid  1=title  2=subtitle  3=stats  4=fadeout
    // keep ref in sync so JSX onClick can read current phase
    const setPhase = (v) => { phase = v; phaseRef.current = v; };
    let phaseT = 0;     // time within phase
    let fadeAlpha = 0;  // final white-flash fade
    let titleDone = false;
    let subtitleDone = false;
    let statsVisible = false;

    // Grid lines
    const GRID_COLS = 28, GRID_ROWS = 18;
    const gW = () => W / GRID_COLS, gH = () => H / GRID_ROWS;

    // Stars
    const stars = Array.from({ length: 220 }, () => ({
      x: Math.random(), y: Math.random(),
      s: Math.random() * 1.8 + 0.3,
      bri: Math.random(),
      spd: Math.random() * 0.0003 + 0.0001,
    }));

    // Particles (flying code symbols)
    const CODE_CHARS = ['</', '{}', '=>', '&&', '||', '[]', '()', '++', ';;', '/*', '*/'];
    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.6,
      vy: (Math.random() - 0.5) * 0.6,
      ch: CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)],
      alpha: Math.random() * 0.4 + 0.08,
      size: Math.floor(Math.random() * 8 + 10),
    }));

    // Scan lines for CRT feel
    const scanLines = Array.from({ length: 6 }, (_, i) => ({
      y: Math.random() * H, spd: Math.random() * 2 + 1, alpha: Math.random() * 0.08 + 0.04,
    }));

    // Title explosion shards
    let shards = [];
    const buildShards = () => {
      shards = Array.from({ length: 40 }, () => ({
        x: W / 2, y: H / 2,
        vx: (Math.random() - 0.5) * 14,
        vy: (Math.random() - 0.5) * 14,
        life: 1, decay: Math.random() * 0.02 + 0.01,
        size: Math.random() * 4 + 1,
        color: [CYAN, PURPLE, GOLD, GREEN][Math.floor(Math.random() * 4)],
      }));
    };

    // Hex ring data
    const hexRings = [
      { r: Math.min(W, H) * 0.42, sides: 6, rot: 0,    spd: 0.0007,  col: CYAN,   lw: 0.5, alpha: 0.15 },
      { r: Math.min(W, H) * 0.33, sides: 6, rot: 0.52, spd: -0.0012, col: PURPLE, lw: 0.8, alpha: 0.18 },
      { r: Math.min(W, H) * 0.23, sides: 6, rot: 1.05, spd: 0.002,   col: GOLD,   lw: 0.6, alpha: 0.2  },
    ];

    // Orbit dots
    const orbits = [
      { r: Math.min(W, H) * 0.38, count: 8, spd: 0.0008, offset: 0,    size: 2.5, col: CYAN   },
      { r: Math.min(W, H) * 0.29, count: 6, spd: -0.0015, offset: 0.3, size: 2,   col: PURPLE },
    ];

    // Data bars (HUD effect)
    const bars = Array.from({ length: 6 }, (_, i) => ({
      x: 40, y: H * 0.15 + i * 32,
      w: Math.random() * 180 + 80, label: ['HTML', 'CSS', 'JS', 'XP', 'COINS', 'LVL'][i],
      col: [CYAN, '#3296ff', GOLD, PURPLE, GOLD, GREEN][i],
      pct: 0, target: Math.random() * 0.7 + 0.3,
    }));

    // Stats counter
    const counters = [
      { label: 'MISSIONS', target: 42, val: 0, x: W - 220, y: H * 0.15 + 0, col: CYAN },
      { label: 'XP TOTAL', target: 9800, val: 0, x: W - 220, y: H * 0.15 + 52, col: PURPLE },
      { label: 'PLAYERS', target: 1337, val: 0, x: W - 220, y: H * 0.15 + 104, col: GOLD },
    ];

    // ── Draw Helpers ───────────────────────────────────────────
    function hexPath(cx, cy, r, sides, rot) {
      ctx.beginPath();
      for (let i = 0; i < sides; i++) {
        const a = rot + (i / sides) * Math.PI * 2;
        const px = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
    }

    function drawGrid(alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = CYAN;
      ctx.lineWidth = 0.3;
      // vertical
      for (let c = 0; c <= GRID_COLS; c++) {
        ctx.beginPath();
        ctx.moveTo(c * gW(), 0); ctx.lineTo(c * gW(), H);
        ctx.stroke();
      }
      // horizontal
      for (let r = 0; r <= GRID_ROWS; r++) {
        ctx.beginPath();
        ctx.moveTo(0, r * gH()); ctx.lineTo(W, r * gH());
        ctx.stroke();
      }
      // grid intersections
      ctx.fillStyle = CYAN;
      for (let c = 0; c <= GRID_COLS; c++) {
        for (let r = 0; r <= GRID_ROWS; r++) {
          ctx.beginPath();
          ctx.arc(c * gW(), r * gH(), 1.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    function drawStars(alpha) {
      ctx.save();
      stars.forEach(s => {
        s.bri += s.spd;
        const b = (Math.sin(s.bri * 3) + 1) / 2;
        ctx.globalAlpha = alpha * (0.3 + b * 0.6);
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(s.x * W, s.y * H, s.s, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }

    function drawParticles(alpha) {
      ctx.save();
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < -40) p.x = W + 40;
        if (p.x > W + 40) p.x = -40;
        if (p.y < -40) p.y = H + 40;
        if (p.y > H + 40) p.y = -40;
        ctx.globalAlpha = alpha * p.alpha;
        ctx.font = `${p.size}px 'Share Tech Mono', monospace`;
        ctx.fillStyle = CYAN;
        ctx.fillText(p.ch, p.x, p.y);
      });
      ctx.restore();
    }

    function drawScanLines(alpha) {
      ctx.save();
      scanLines.forEach(s => {
        s.y += s.spd;
        if (s.y > H) s.y = -2;
        ctx.globalAlpha = alpha * s.alpha;
        ctx.fillStyle = CYAN;
        ctx.fillRect(0, s.y, W, 1.5);
      });
      ctx.restore();
    }

    function drawHexRings(alpha) {
      ctx.save();
      hexRings.forEach(h => {
        h.rot += h.spd;
        ctx.globalAlpha = alpha * h.alpha;
        ctx.strokeStyle = h.col;
        ctx.lineWidth = h.lw;
        hexPath(W / 2, H / 2, h.r, h.sides, h.rot);
        ctx.stroke();
      });
      ctx.restore();
    }

    function drawOrbits(alpha) {
      ctx.save();
      orbits.forEach(o => {
        o.offset += o.spd;
        for (let i = 0; i < o.count; i++) {
          const a = o.offset + (i / o.count) * Math.PI * 2;
          const dx = W / 2 + Math.cos(a) * o.r;
          const dy = H / 2 + Math.sin(a) * o.r;
          ctx.globalAlpha = alpha * 0.7;
          ctx.fillStyle = o.col;
          ctx.beginPath();
          ctx.arc(dx, dy, o.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      ctx.restore();
    }

    function drawBars(alpha) {
      ctx.save();
      bars.forEach(b => {
        if (b.pct < b.target) b.pct = Math.min(b.target, b.pct + 0.012);
        ctx.globalAlpha = alpha;
        // track
        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.lineWidth = 1;
        ctx.strokeRect(b.x, b.y, 200, 12);
        // fill
        ctx.fillStyle = b.col;
        ctx.globalAlpha = alpha * 0.7;
        ctx.fillRect(b.x + 1, b.y + 1, (200 - 2) * b.pct, 10);
        // label
        ctx.globalAlpha = alpha;
        ctx.fillStyle = b.col;
        ctx.font = "bold 9px 'Orbitron', monospace";
        ctx.letterSpacing = '2px';
        ctx.fillText(b.label, b.x + 208, b.y + 10);
      });
      ctx.restore();
    }

    function drawCounters(alpha) {
      ctx.save();
      counters.forEach(c => {
        if (c.val < c.target) c.val = Math.min(c.target, c.val + Math.ceil(c.target / 80));
        ctx.globalAlpha = alpha;
        ctx.font = "bold 11px 'Orbitron', monospace";
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.fillText(c.label, c.x, c.y + 12);
        ctx.font = "bold 22px 'Orbitron', monospace";
        ctx.fillStyle = c.col;
        ctx.fillText(c.val.toLocaleString(), c.x, c.y + 38);
      });
      ctx.restore();
    }

    function drawShards(alpha) {
      ctx.save();
      shards = shards.filter(s => s.life > 0);
      shards.forEach(s => {
        s.x += s.vx; s.y += s.vy; s.vy += 0.15; s.life -= s.decay;
        ctx.globalAlpha = alpha * s.life;
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }

    function drawCircuitLines(alpha) {
      const lines = [
        [0, H * 0.1,  W * 0.25, H * 0.1,  W * 0.25, H * 0.25, W * 0.35, H * 0.25],
        [W, H * 0.9,  W * 0.75, H * 0.9,  W * 0.75, H * 0.75, W * 0.65, H * 0.75],
        [0, H * 0.8,  W * 0.18, H * 0.8,  W * 0.18, H * 0.6,  W * 0.28, H * 0.6 ],
        [W, H * 0.2,  W * 0.82, H * 0.2,  W * 0.82, H * 0.4,  W * 0.72, H * 0.4 ],
      ];
      ctx.save();
      ctx.strokeStyle = CYAN;
      ctx.lineWidth = 0.8;
      lines.forEach(pts => {
        ctx.globalAlpha = alpha * 0.22;
        ctx.beginPath();
        for (let i = 0; i < pts.length; i += 2) {
          i === 0 ? ctx.moveTo(pts[i], pts[i+1]) : ctx.lineTo(pts[i], pts[i+1]);
        }
        ctx.stroke();
        // dot at each junction
        ctx.fillStyle = CYAN;
        ctx.globalAlpha = alpha * 0.4;
        for (let i = 2; i < pts.length - 2; i += 2) {
          ctx.beginPath(); ctx.arc(pts[i], pts[i+1], 2.5, 0, Math.PI * 2); ctx.fill();
        }
      });
      ctx.restore();
    }

    // ── Title drawing ──────────────────────────────────────────
    function drawTitle(alpha, scale = 1) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Glow rings behind title
      [0.04, 0.07, 0.11].forEach((a, i) => {
        ctx.globalAlpha = alpha * a;
        ctx.beginPath();
        ctx.arc(W / 2, H / 2 - 18, (80 + i * 55) * scale, 0, Math.PI * 2);
        ctx.strokeStyle = CYAN;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      const fs = Math.min(W * 0.12, 120) * scale;
      ctx.globalAlpha = alpha;

      // Shadow
      ctx.shadowColor = CYAN;
      ctx.shadowBlur = 40 * scale;
      ctx.font = `900 ${fs}px 'Orbitron', monospace`;
      ctx.fillStyle = '#ffffff';
      ctx.fillText('CODRION', W / 2, H / 2 - 16);
      ctx.shadowBlur = 0;

      // Underline
      ctx.globalAlpha = alpha * 0.6;
      const uw = fs * 3.6;
      const ux = W / 2 - uw / 2;
      const uy = H / 2 + fs * 0.55;
      const grad = ctx.createLinearGradient(ux, uy, ux + uw, uy);
      grad.addColorStop(0, 'transparent');
      grad.addColorStop(0.3, CYAN);
      grad.addColorStop(0.7, PURPLE);
      grad.addColorStop(1, 'transparent');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(ux, uy); ctx.lineTo(ux + uw, uy);
      ctx.stroke();

      ctx.restore();
    }

    function drawSubtitle(alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const taglines = [
        { t: 'LEARN.  CODE.  DOMINATE.', col: '#ffffff',   fs: Math.min(W * 0.028, 26), y: H / 2 + 70 },
        { t: 'HTML  ·  CSS  ·  JAVASCRIPT', col: CYAN, fs: Math.min(W * 0.017, 16), y: H / 2 + 108 },
      ];
      taglines.forEach(l => {
        ctx.font = `700 ${l.fs}px 'Orbitron', monospace`;
        ctx.fillStyle = l.col;
        ctx.fillText(l.t, W / 2, l.y);
      });
      ctx.restore();
    }

    function drawPressKey(alpha) {
      const pulse = (Math.sin(t * 0.08) + 1) / 2;
      ctx.save();
      ctx.globalAlpha = alpha * (0.4 + pulse * 0.55);
      ctx.textAlign = 'center';
      ctx.font = `700 ${Math.min(W * 0.014, 14)}px 'Orbitron', monospace`;
      ctx.fillStyle = GOLD;
      ctx.fillText('[ PRESS ANY KEY OR CLICK TO BEGIN ]', W / 2, H - 48);
      ctx.restore();
    }

    // ── Main render loop ───────────────────────────────────────
    const PHASE_DURATIONS = [140, 90, 80, 220, 50]; // frames

    function render() {
      if (doneRef.current) return;
      rafRef.current = requestAnimationFrame(render);
      t++;
      phaseT++;
      if (phaseT >= PHASE_DURATIONS[phase]) { setPhase(Math.min(phase + 1, 4)); phaseT = 0; }

      // Background
      ctx.fillStyle = '#060810';
      ctx.fillRect(0, 0, W, H);

      const ease = (x) => x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

      // ── Phase 0: Grid materialises ─────────────────────────
      if (phase >= 0) {
        const a = phase === 0 ? ease(Math.min(phaseT / 60, 1)) * 0.25 : 0.25;
        drawGrid(a);
        drawStars(phase === 0 ? ease(Math.min(phaseT / 80, 1)) : 1);
        drawCircuitLines(phase === 0 ? ease(Math.min(phaseT / 100, 1)) : 1);
        drawParticles(0.6);
        drawScanLines(0.5);
      }

      // ── Phase 1+: Hex rings and orbits ─────────────────────
      if (phase >= 1) {
        const a = phase === 1 ? ease(Math.min(phaseT / 50, 1)) : 1;
        drawHexRings(a);
        drawOrbits(a);
      }

      // ── Phase 1: Title slams in ────────────────────────────
      if (phase >= 1) {
        const raw = phase === 1 ? phaseT / PHASE_DURATIONS[1] : 1;
        const a = Math.min(raw * 1.5, 1);
        const scl = phase === 1 ? (0.4 + ease(raw) * 0.6) : 1;
        drawTitle(a, scl);
        // Spawn shards when title first appears
        if (phase === 1 && phaseT === 1) buildShards();
        drawShards(a);
      }

      // ── Phase 2: Subtitle fades in ─────────────────────────
      if (phase >= 2) {
        const a = phase === 2 ? ease(Math.min(phaseT / PHASE_DURATIONS[2], 1)) : 1;
        drawTitle(1, 1);
        drawSubtitle(a);
      }

      // ── Phase 3: HUD stats animate ─────────────────────────
      if (phase >= 3) {
        const a = phase === 3 ? ease(Math.min(phaseT / 60, 1)) : 1;
        drawTitle(1, 1);
        drawSubtitle(1);
        drawBars(a);
        drawCounters(a);
        drawPressKey(a);
      }

      // ── Phase 4: White-flash exit ──────────────────────────
      if (phase >= 4) {
        fadeAlpha = Math.min(fadeAlpha + 0.045, 1);
        ctx.fillStyle = `rgba(6,8,16,${fadeAlpha})`;
        ctx.fillRect(0, 0, W, H);
        if (fadeAlpha >= 1) { doneRef.current = true; phaseRef.current = 5; onComplete(); return; }
      }
    }

    render();

    // ── Input to skip ──────────────────────────────────────────
    const onKey   = () => phaseRef.current >= 3 && skip();
    const onClick = () => phaseRef.current >= 3 && skip();
    window.addEventListener('keydown', onKey);
    canvas.addEventListener('click', onClick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
      canvas.removeEventListener('click', onClick);
    };
  }, [skip, onComplete]);

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'#060810', cursor:'pointer' }}
      onClick={() => phaseRef.current >= 3 && skip()}>
      <canvas ref={canvasRef} style={{ width:'100%', height:'100%', display:'block' }} />
      <div style={{
        position:'absolute', bottom:20, right:24,
        fontFamily:"'Orbitron',monospace", fontSize:'10px', letterSpacing:'2px',
        color:'rgba(0,245,255,0.3)', pointerEvents:'none',
      }}>v1.0.0 — CODRION ENGINE</div>
    </div>
  );
}
